import { useEffect, useState } from "react";
import api, { fileUrl } from "../../lib/api";
import { Plus, Trash2, Pencil, Save, X, Upload as UploadIcon, Copy } from "lucide-react";

const empty = { title: "", slug: "", excerpt: "", body: "", image_url: "", category: "Story", author: "YACRRD Team", published: true };
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export default function NewsEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/news?all=true").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const save = async (n) => {
        if (n.id) await api.put(`/news/${n.id}`, n); else await api.post("/news", n);
        setEditing(null); load();
    };
    const del = async (id) => { if (!window.confirm("Delete story?")) return; await api.delete(`/news/${id}`); load(); };
    const dup = async (id) => { await api.post(`/news/${id}/duplicate`); load(); };

    return (
        <div data-testid="news-editor">
            <div className="flex items-center justify-between mb-8">
                <div><h1 className="font-display text-4xl mb-2">News & Stories</h1><p className="text-muted-foreground">Publish project updates, field stories, and impact reports.</p></div>
                <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="add-news"><Plus size={14} /> New Story</button>
            </div>
            <div className="bg-card border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        <tr><th className="text-left p-4">Title</th><th className="text-left p-4">Category</th><th className="text-left p-4">Published</th><th className="text-right p-4">Actions</th></tr>
                    </thead>
                    <tbody>
                        {items.map((n) => (
                            <tr key={n.id} className="border-t border-border">
                                <td className="p-4 font-medium">{n.title}</td>
                                <td className="p-4 text-muted-foreground">{n.category}</td>
                                <td className="p-4">{n.published ? <span className="text-secondary">● Yes</span> : <span className="text-muted-foreground">○ No</span>}</td>
                                <td className="p-4 text-right space-x-2">
                                    <button onClick={() => setEditing({ ...n })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs"><Pencil size={12} /> Edit</button>
                                    <button onClick={() => dup(n.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs" data-testid={`dup-news-${n.slug}`}><Copy size={12} /> Duplicate</button>
                                    <button onClick={() => del(n.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive text-xs"><Trash2 size={12} /> Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editing && <Modal item={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
        </div>
    );
}

const Modal = ({ item, onChange, onSave, onClose }) => {
    const set = (k, v) => onChange({ ...item, [k]: v });
    const upload = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        set("image_url", data.url);
    };
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto">
            <div className="bg-card border border-border rounded-sm w-full max-w-3xl my-10">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="font-display text-2xl">{item.id ? "Edit Story" : "New Story"}</h2>
                    <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <F label="Title" value={item.title} onChange={(v) => onChange({ ...item, title: v, slug: item.id ? item.slug : slugify(v) })} testid="news-title" />
                    <F label="Slug" value={item.slug} onChange={(v) => set("slug", slugify(v))} testid="news-slug" />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <F label="Category" value={item.category} onChange={(v) => set("category", v)} testid="news-category" />
                        <F label="Author" value={item.author} onChange={(v) => set("author", v)} testid="news-author" />
                    </div>
                    <T label="Excerpt" value={item.excerpt} onChange={(v) => set("excerpt", v)} rows={2} testid="news-excerpt" />
                    <T label="Body (full article)" value={item.body} onChange={(v) => set("body", v)} rows={10} testid="news-body" />
                    <div>
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Cover image</span>
                        <div className="flex items-center gap-3">
                            <input value={item.image_url || ""} onChange={(e) => set("image_url", e.target.value)} className="flex-1 px-3 py-2.5 rounded-sm border border-border bg-background text-sm" />
                            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border cursor-pointer text-sm"><UploadIcon size={14} /> Upload
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                            </label>
                        </div>
                        {item.image_url && <img src={fileUrl(item.image_url)} alt="" className="mt-3 h-32 rounded-sm border border-border object-cover" />}
                    </div>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!item.published} onChange={(e) => set("published", e.target.checked)} /><span className="text-sm">Published</span></label>
                </div>
                <div className="p-6 border-t border-border flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 rounded-sm border border-border text-sm">Cancel</button>
                    <button onClick={() => onSave(item)} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="news-save"><Save size={14} /> Save</button>
                </div>
            </div>
        </div>
    );
};

const F = ({ label, value, onChange, testid }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" /></label>
);
const T = ({ label, value, onChange, rows = 3, testid }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" /></label>
);
